import { dayProgress, toDozenal } from "./time.js";

/**
 * The two small clock faces in the sidebar, drawn straight onto 2D canvases.
 * They are deliberately independent of the game renderer so the homepage costs
 * nothing when the game is not running.
 */
const COLORS = {
  background: "#1a1a1a",
  border: "#252525",
  title: "#cccccc",
  text: "#ffffff",
  barBackground: "#333333",
  barFill: "#7FDBFF",
  arc1: "#40E0D0",
  arc2: "#7DF9FF",
  arc3: "#FF69B4",
  arc4: "#DA70D6",
  arcFill: "#F012BE",
};

/** Sizes the backing store to the element and returns a scaled context. */
function prepare(canvas: HTMLCanvasElement): { ctx: CanvasRenderingContext2D; w: number; h: number } | null {
  const width = canvas.clientWidth;
  const height = canvas.clientHeight;
  if (width <= 0 || height <= 0) return null;

  const ratio = window.devicePixelRatio || 1;
  const backingWidth = Math.round(width * ratio);
  const backingHeight = Math.round(height * ratio);
  if (canvas.width !== backingWidth || canvas.height !== backingHeight) {
    canvas.width = backingWidth;
    canvas.height = backingHeight;
  }

  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
  ctx.clearRect(0, 0, width, height);
  return { ctx, w: width, h: height };
}

function drawPanel(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  ctx.fillStyle = COLORS.background;
  ctx.strokeStyle = COLORS.border;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.roundRect(0, 0, w, h, 6);
  ctx.fill();
  ctx.stroke();
}

export function drawChronClock(canvas: HTMLCanvasElement, large: boolean): void {
  const surface = prepare(canvas);
  if (!surface) return;
  const { ctx, w, h } = surface;

  const progress = dayProgress();
  drawPanel(ctx, w, h);

  const fontSize = Math.min(w, h) * (large ? 0.18 : 0.25);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  ctx.fillStyle = COLORS.title;
  ctx.font = `${fontSize * 0.4}px 'JetBrains Mono', monospace`;
  ctx.fillText("Chrons", w / 2, large ? h * 0.15 : h * 0.2);

  ctx.fillStyle = COLORS.text;
  ctx.font = `bold ${fontSize}px 'JetBrains Mono', monospace`;
  ctx.fillText((progress * 100).toFixed(3).padStart(6, "0"), w / 2, large ? h * 0.35 : h * 0.45);

  const barWidth = w * 0.8;
  const barHeight = large ? h * 0.06 : h * 0.12;
  const barX = (w - barWidth) / 2;
  const barY = large ? h * 0.5 : h * 0.7;

  ctx.fillStyle = COLORS.barBackground;
  ctx.fillRect(barX, barY, barWidth, barHeight);
  ctx.fillStyle = COLORS.barFill;
  ctx.fillRect(barX, barY, barWidth * progress, barHeight);
  ctx.strokeStyle = COLORS.border;
  ctx.lineWidth = 1;
  ctx.strokeRect(barX, barY, barWidth, barHeight);
}

const DUOD_ARCS = [
  { multiplier: 1, stepped: true, radius: 0.85, width: 0.08, color: COLORS.arc1 },
  { multiplier: 12, stepped: true, radius: 0.68, width: 0.08, color: COLORS.arc2 },
  { multiplier: 144, stepped: true, radius: 0.51, width: 0.08, color: COLORS.arcFill },
  { multiplier: 1728, stepped: false, radius: 0.34, width: 0.08, color: COLORS.arc3 },
  { multiplier: 20736, stepped: false, radius: 0.12, width: 0.06, color: COLORS.arc4 },
];

export function drawDuodClock(canvas: HTMLCanvasElement, large: boolean): void {
  const surface = prepare(canvas);
  if (!surface) return;
  const { ctx, w, h } = surface;

  const progress = dayProgress();
  drawPanel(ctx, w, h);

  const cx = w / 2;
  const cy = h * 0.4;
  const radius = Math.min(w, h) * (large ? 0.3 : 0.32);

  ctx.fillStyle = "#111";
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fill();

  const start = -Math.PI / 2;
  for (const arc of DUOD_ARCS) {
    const value = arc.stepped
      ? (Math.floor(progress * 12 * arc.multiplier) % 12) / 12
      : (progress * arc.multiplier) % 1;
    ctx.beginPath();
    ctx.arc(cx, cy, radius * arc.radius, start, start + value * Math.PI * 2);
    ctx.strokeStyle = arc.color;
    ctx.lineWidth = radius * arc.width;
    ctx.lineCap = "butt";
    ctx.stroke();
  }

  ctx.fillStyle = COLORS.text;
  ctx.font = `bold ${Math.min(w, h) * (large ? 0.1 : 0.14)}px 'JetBrains Mono', monospace`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(`.${toDozenal(progress)}`, cx, large ? h * 0.78 : h * 0.82);
}

/** Keeps the sidebar widgets ticking; returns a stop function. */
export function startClockWidgets(onOpen: (kind: string) => void): () => void {
  // The selector is deliberately narrow: the sidebar also holds link tiles
  // that share the widget styling but have no clock to draw.
  const widgets = Array.from(document.querySelectorAll<HTMLElement>(".clock-widget[data-clock]"));
  const listeners: Array<() => void> = [];

  for (const widget of widgets) {
    const open = () => onOpen(widget.dataset.clock!);
    widget.addEventListener("click", open);
    listeners.push(() => widget.removeEventListener("click", open));
  }

  let frame = 0;
  const tick = () => {
    for (const widget of widgets) {
      const canvas = widget.querySelector<HTMLCanvasElement>(".clock-canvas");
      if (!canvas) continue;
      if (widget.dataset.clock === "chron") drawChronClock(canvas, false);
      else drawDuodClock(canvas, false);
    }
    frame = requestAnimationFrame(tick);
  };
  frame = requestAnimationFrame(tick);

  return () => {
    cancelAnimationFrame(frame);
    for (const off of listeners) off();
  };
}
